/**
 * Geometry for the square crop viewport in the profile-photo dialog.
 *
 * Kept away from the component so the arithmetic is unit-testable: the dialog
 * only owns pointer events and the canvas call, everything that can be off by
 * a factor of `zoom` lives here.
 *
 * Model: the image is drawn centred in a square viewport, scaled so it always
 * *covers* it, then nudged by a centre-relative offset in viewport pixels.
 * Offsets are clamped so no empty edge can ever enter the crop.
 */

/** Side of the exported square, in pixels. Ample for a ~28mm print circle. */
export const PROFILE_PHOTO_OUTPUT_SIZE = 512;

export const MIN_PHOTO_ZOOM = 1;
export const MAX_PHOTO_ZOOM = 4;
export const PHOTO_ZOOM_STEP = 0.01;

export interface PhotoCropTransform {
  /** 1 = image exactly covers the viewport. */
  zoom: number;
  /** Horizontal nudge from centre, in viewport pixels. */
  offsetX: number;
  /** Vertical nudge from centre, in viewport pixels. */
  offsetY: number;
}

export interface PhotoCropInput {
  naturalWidth: number;
  naturalHeight: number;
  /** Side of the square preview area, in CSS pixels. */
  viewportSize: number;
  transform: PhotoCropTransform;
}

export interface PhotoCropRect {
  /** Source rectangle in the *original* image's pixel space. */
  sourceX: number;
  sourceY: number;
  sourceSize: number;
}

export const IDENTITY_PHOTO_CROP_TRANSFORM: PhotoCropTransform = {
  zoom: MIN_PHOTO_ZOOM,
  offsetX: 0,
  offsetY: 0,
};

function isPositive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Scale at which the image just covers the viewport — the short edge lands
 * exactly on the viewport, the long edge overflows.
 */
export function getCoverScale(naturalWidth: number, naturalHeight: number, viewportSize: number): number {
  if (!isPositive(naturalWidth) || !isPositive(naturalHeight) || !isPositive(viewportSize)) {
    return 1;
  }
  return viewportSize / Math.min(naturalWidth, naturalHeight);
}

/** How far the image may be nudged along one axis before an edge shows. */
export function getMaxOffset(naturalSize: number, scale: number, viewportSize: number): number {
  const displayedSize = naturalSize * scale;
  return Math.max(0, (displayedSize - viewportSize) / 2);
}

export function clampPhotoZoom(zoom: number): number {
  return clamp(zoom, MIN_PHOTO_ZOOM, MAX_PHOTO_ZOOM);
}

/**
 * Bring a transform back inside the legal range. Called after every drag and
 * after every zoom change — zooming out shrinks the offset budget, so a
 * previously valid pan can become illegal.
 */
export function clampPhotoCropTransform(input: PhotoCropInput): PhotoCropTransform {
  const { naturalWidth, naturalHeight, viewportSize, transform } = input;
  const zoom = clampPhotoZoom(transform.zoom);

  if (!isPositive(naturalWidth) || !isPositive(naturalHeight) || !isPositive(viewportSize)) {
    return { ...IDENTITY_PHOTO_CROP_TRANSFORM, zoom };
  }

  const scale = getCoverScale(naturalWidth, naturalHeight, viewportSize) * zoom;
  const maxOffsetX = getMaxOffset(naturalWidth, scale, viewportSize);
  const maxOffsetY = getMaxOffset(naturalHeight, scale, viewportSize);

  return {
    zoom,
    offsetX: clamp(transform.offsetX, -maxOffsetX, maxOffsetX),
    offsetY: clamp(transform.offsetY, -maxOffsetY, maxOffsetY),
  };
}

/**
 * The square of the original image that the viewport is currently showing —
 * i.e. exactly the `drawImage` source rectangle the dialog needs.
 */
export function getPhotoCropRect(input: PhotoCropInput): PhotoCropRect {
  const { naturalWidth, naturalHeight, viewportSize } = input;

  if (!isPositive(naturalWidth) || !isPositive(naturalHeight) || !isPositive(viewportSize)) {
    return { sourceX: 0, sourceY: 0, sourceSize: 0 };
  }

  const transform = clampPhotoCropTransform(input);
  const scale = getCoverScale(naturalWidth, naturalHeight, viewportSize) * transform.zoom;

  // Viewport edge, expressed in image pixels: half the displayed image minus
  // half the viewport, shifted back by the pan.
  const sourceSize = viewportSize / scale;
  const sourceX = (naturalWidth * scale - viewportSize) / 2 / scale - transform.offsetX / scale;
  const sourceY = (naturalHeight * scale - viewportSize) / 2 / scale - transform.offsetY / scale;

  return {
    sourceX: clamp(sourceX, 0, Math.max(0, naturalWidth - sourceSize)),
    sourceY: clamp(sourceY, 0, Math.max(0, naturalHeight - sourceSize)),
    sourceSize,
  };
}

/** CSS `width`/`height`/`transform` for the `<img>` inside the viewport. */
export function getPhotoPreviewStyle(input: PhotoCropInput): {
  width: number;
  height: number;
  translateX: number;
  translateY: number;
} {
  const { naturalWidth, naturalHeight, viewportSize } = input;

  if (!isPositive(naturalWidth) || !isPositive(naturalHeight) || !isPositive(viewportSize)) {
    return { width: 0, height: 0, translateX: 0, translateY: 0 };
  }

  const transform = clampPhotoCropTransform(input);
  const scale = getCoverScale(naturalWidth, naturalHeight, viewportSize) * transform.zoom;

  return {
    width: naturalWidth * scale,
    height: naturalHeight * scale,
    translateX: transform.offsetX,
    translateY: transform.offsetY,
  };
}
